import { Alert, Box, Stack } from "@mui/material";
import { ReconnectingOverlay } from "components/scrapEyeVideoPlayer/ReconnectingOverlay";
import { useTenantTranslation } from "hooks/formatters";
import { useHlsVideo } from "hooks/useHlsVideo";
import { useCallback, useEffect, useState } from "react";
import { RadioTransport } from "./RadioTransport";
import { TranscriptView } from "./TranscriptView";
import { useRadioTranscript } from "./hooks";
import { useTranscriptSync } from "./transcriptSync";

// Long enough to clear a missed phrase, short enough to stay inside one
// transmission: DMR calls here run a few seconds to under a minute.
const SKIP_SECONDS = 15;

const HELD_SEEK_RETRY_MS = 150;
const HELD_SEEK_TIMEOUT_MS = 10_000;

type Props = Readonly<{
  deviceId: number;
  srcUrl: string | null;
  startTimeIso: string;
  endTimeIso: string;
  receiverId: number | null;
  allChannels: boolean;
  channelLabels: ReadonlyMap<number, string>;
  onSelectChannel: (receiverId: number) => void;
}>;

export const HistoricalRadioContent = ({
  deviceId,
  srcUrl,
  startTimeIso,
  endTimeIso,
  receiverId,
  allChannels,
  channelLabels,
  onSelectChannel,
}: Props) => {
  const { t } = useTenantTranslation();

  const {
    ref,
    fatalError,
    isReconnecting,
    getPlayingDate,
    seekToDate,
    skipSeconds,
  } = useHlsVideo({
    srcUrl: srcUrl ?? "",
    isLive: false,
    autoPlay: false,
  });

  const transcript = useRadioTranscript({
    deviceId,
    receiverId,
    allChannels,
    startTime: startTimeIso,
    endTime: endTimeIso,
    skip: srcUrl == null,
  });

  const { lines, activeWordKey } = useTranscriptSync(
    transcript,
    getPlayingDate,
    ref,
    {
      // Labelled whatever the scope, so a block always names the talkgroup it
      // transcribes and matches the clips filed under it.
      channelLabels,
      playingReceiverId: receiverId,
    }
  );

  const playFrom = useCallback(
    (startMs: number): boolean => {
      const media = ref.current;
      if (!media || !seekToDate(startMs)) return false;
      media.play().catch(() => undefined);
      return true;
    },
    [seekToDate, ref]
  );

  const [heldSeekMs, setHeldSeekMs] = useState<number | null>(null);

  const seekToLine = useCallback(
    (startMs: number, lineReceiverId: number | null) => {
      if (lineReceiverId != null && lineReceiverId !== receiverId) {
        setHeldSeekMs(startMs);
        onSelectChannel(lineReceiverId);
        return;
      }
      if (!playFrom(startMs)) setHeldSeekMs(startMs);
    },
    [receiverId, onSelectChannel, playFrom]
  );

  useEffect(() => {
    if (heldSeekMs == null) return;
    const giveUpAt = Date.now() + HELD_SEEK_TIMEOUT_MS;
    const intervalId = window.setInterval(() => {
      if (playFrom(heldSeekMs) || Date.now() > giveUpAt) {
        setHeldSeekMs(null);
        clearInterval(intervalId);
      }
    }, HELD_SEEK_RETRY_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [heldSeekMs, playFrom]);

  return (
    <Stack spacing={2}>
      <Box sx={{ position: "relative", width: "100%" }}>
        <RadioTransport
          mediaRef={ref}
          onSkip={skipSeconds}
          skipSeconds={SKIP_SECONDS}
        />
        {isReconnecting ? <ReconnectingOverlay /> : null}
        {fatalError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {t("radioPlaybackError")}
          </Alert>
        ) : null}
      </Box>
      <TranscriptView
        lines={lines}
        activeWordKey={activeWordKey}
        onChannelClick={onSelectChannel}
        onLineClick={seekToLine}
      />
    </Stack>
  );
};
